import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { LayoutService } from '../../core/services/layout/layout.service';
import { ProjectStatusComponent } from '../../pages/project-status/project-status';
import { NotificationsComponent } from '../../shared/components/notifications/notifications';
import { combineLatest } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TaskStatus } from '../../pages/projects/tasks/task-status/task-status';
import { IssueStatus } from '../../pages/projects/issues/issue-status/issue-status';
import { RevisionStatus } from '../../pages/projects/revisions/revision-status/revision-status';
import { RequestStatus } from '../../pages/projects/requests/request-status/request-status';
import { StatusSidebarRefreshService } from '@app/core/services/sidebar/status-sidebar-refresh/status-sidebar-refresh.service';

@Component({
  selector: 'app-right-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    ProjectStatusComponent,
    NotificationsComponent,
    TaskStatus,
    IssueStatus,
    RevisionStatus,
    RequestStatus,
  ],
  templateUrl: './right-sidebar.html',
  styleUrls: ['./right-sidebar.scss'],
})
export class RightSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ProjectStatusComponent)
  projectStatusComponent?: ProjectStatusComponent;
  private destroy$ = new Subject<void>();

  // Add property to track if we're on tasks page
  isOnTasksPage: boolean = false;
  currentProjectId: string | null = null;
  isOnTaskDetailsPage: boolean = false;
  currentTaskId: string | null = null;
  isOnIssueDetailsPage: boolean = false;
  isOnRevisionDetailsPage: boolean = false;
  isOnRequestDetailsPage: boolean = false;
  currentIssueId: string | null = null;
  currentRevisionId: string | null = null;
  currentRequestId: string | null = null;
  isOnDashboardPage: boolean = false;

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    public statusSidebarRefreshService: StatusSidebarRefreshService
  ) {}

  ngOnInit(): void {
    // Listen to route changes to detect tasks page
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.checkIfOnTasksPage();
      });

    // Check initial route
    this.checkIfOnTasksPage();
  }

  private checkIfOnTasksPage(): void {
    const currentUrl = this.router.url;
    // Check if we're on dashboard page
    this.isOnDashboardPage = /^\/dashboard/.test(currentUrl);
    
    // Check if we're on task details page (individual task)
    //  this.isOnTaskDetailsPage = /^\/task\/task-details\/[^\/]+$/.test(currentUrl);
    this.isOnTaskDetailsPage = /^\/task\/task-details\/[^\/]+/.test(currentUrl);

    // Check if we're on issue details page (individual issue)
    this.isOnIssueDetailsPage = /^\/issue\/issue-details\/[^\/]+/.test(
      currentUrl
    );

    // Check if we're on revision details page (individual revision)
    this.isOnRevisionDetailsPage = /^\/revision\/revision-details\/[^\/]+/.test(
      currentUrl
    );

    // Check if we're on project tasks page (list of tasks within a project)
    this.isOnTasksPage = /^\/projects\/project-details\/[^\/]+\/tasks$/.test(
      currentUrl
    );

    // Check if we're on request details page (individual request)
    this.isOnRequestDetailsPage = /^\/request\/request-details\/[^\/]+/.test(
      currentUrl
    );

    if (this.isOnTaskDetailsPage) {
      // Extract task ID from URL
      this.currentTaskId = this.getTaskIdFromUrl();
    } else if (this.isOnIssueDetailsPage) {
      // Extract issue ID from URL
      this.currentIssueId = this.getIssueIdFromUrl();
    } else if (this.isOnRevisionDetailsPage) {
      // Extract revision ID from URL
      this.currentRevisionId = this.getRevisionIdFromUrl();
    } else if (this.isOnTasksPage) {
      this.currentProjectId = this.getProjectIdFromUrl();
    } else if (this.isOnRequestDetailsPage) {
      // Extract request ID from URL
      this.currentRequestId = this.getRequestIdFromUrl();
    }
  }

  private getProjectIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(
      /\/projects\/project-details\/([^\/]+)\/tasks/
    );
    return match ? match[1] : null;
  }
  private getTaskIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    // const match = currentUrl.match(/\/task\/task-details\/([^\/]+)$/);
    const match = currentUrl.match(/\/task\/task-details\/([^\/]+)/);
    return match ? match[1] : null;
  }
  private getIssueIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(/\/issue\/issue-details\/([^\/]+)/);
    return match ? match[1] : null;
  }
  private getRevisionIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(/\/revision\/revision-details\/([^\/]+)/);
    return match ? match[1] : null;
  }
  private getRequestIdFromUrl(): string | null {
    const currentUrl = this.router.url;
    const match = currentUrl.match(/\/request\/request-details\/([^\/]+)/);
    return match ? match[1] : null;
  }

  ngAfterViewInit(): void {
    // Listen for changes in right sidebar content
    this.layoutService.rightSidebarContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => {
        if (content === 'project-status') {
          // Give the component time to render, then force chart initialization
          setTimeout(() => {
            if (this.projectStatusComponent) {
              this.projectStatusComponent.forceChartInitialization();
            }
          }, 300);
        }
      });

    // Listen for sidebar collapse/expand events and content changes together
    combineLatest([
      this.layoutService.isRightSidebarCollapsed$,
      this.layoutService.rightSidebarContent$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isCollapsed, content]) => {
        if (!isCollapsed && content === 'project-status') {
          // When sidebar is expanded and project-status is active, reinitialize charts
          setTimeout(() => {
            if (this.projectStatusComponent) {
              this.projectStatusComponent.forceChartInitialization();
            }
          }, 200);
        }
      });
      // Listen for project completion modal trigger from project-details
    this.layoutService.openProjectCompletionModal$
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      // Ensure project-status component is available and call its method
      if (this.projectStatusComponent) {
        this.projectStatusComponent.openProjectCompletionModal();
      } else {
        // If component not ready, wait a bit and try again
        setTimeout(() => {
          if (this.projectStatusComponent) {
            this.projectStatusComponent.openProjectCompletionModal();
          }
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
